import React, { useState } from 'react';
import api from '../../services/api';

interface AddFundsProps {
  onClose: () => void;
  onSuccess: () => void;
}

const AddFunds: React.FC<AddFundsProps> = ({ onClose, onSuccess }) => {
  const [amount, setAmount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const predefinedAmounts = [500, 1000, 2000, 5000, 10000];

  const handleInitializePayment = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (parsedAmount < 100) {
      setError('Minimum top-up amount is ₦100');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/wallet/topup/initialize', {
        amount: parsedAmount
      });

      if (response.data.status === 'success') {
        // Store reference in session storage for verification
        sessionStorage.setItem('payment_reference', response.data.reference);
        
        // Redirect to Paystack payment page
        // Paystack will redirect back to our callback URL after payment
        window.location.href = response.data.authorization_url;
      } else {
        setError(response.data.message || 'Failed to initialize payment');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAmountClick = (value: number) => {
    setAmount(value.toString());
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">Add Funds</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <label className="block text-gray-700 font-medium mb-2">
            Enter Amount (NGN)
          </label>
          <input
            type="number"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError('');
            }}
            placeholder="Enter amount"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            min="100"
            step="1"
          />
          <p className="text-sm text-gray-500 mt-1">Minimum: ₦100</p>
        </div>

        <div className="mb-6">
          <p className="text-gray-700 font-medium mb-3">Quick Select:</p>
          <div className="grid grid-cols-3 gap-3">
            {predefinedAmounts.map((value) => (
              <button
                key={value}
                onClick={() => handleAmountClick(value)}
                className={`px-4 py-2 rounded-lg border-2 font-medium transition-all ${
                  amount === value.toString()
                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-gray-300 hover:border-blue-400 text-gray-700'
                }`}
              >
                ₦{value.toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleInitializePayment}
            disabled={loading || !amount}
            className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Processing...' : 'Proceed to Payment'}
          </button>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Secure Payment:</strong> You will be redirected to Paystack to complete your payment securely.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddFunds;
